import React, { useRef, useState, useCallback, useEffect } from "react";
import { GripVertical } from "lucide-react";

// Draggable before/after image comparison slider.
// Props: beforeUrl, afterUrl, beforeLabel?, afterLabel?, initial? (0..100), className?
export default function BeforeAfter({ beforeUrl, afterUrl, beforeLabel = "Før", afterLabel = "Etter", initial = 50, className = "" }) {
  const wrap = useRef(null);
  const [pct, setPct] = useState(initial);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const el = wrap.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    setPct(p);
  }, []);

  useEffect(() => {
    const onMove = (e) => { if (!dragging.current) return; const x = e.touches ? e.touches[0].clientX : e.clientX; move(x); };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [move]);

  const start = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    move(x);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 3));
    if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 3));
  };

  if (!beforeUrl || !afterUrl) return null;

  return (
    <div
      ref={wrap}
      className={`relative w-full overflow-hidden rounded-xl select-none touch-none bg-[#0F1210] ${className}`}
      onMouseDown={start}
      onTouchStart={start}
      data-testid="before-after"
    >
      {/* After image (bottom layer full) */}
      <img src={afterUrl} alt={afterLabel} className="block w-full h-auto pointer-events-none" draggable="false" />
      {/* Before image clipped by pct */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pct}%` }}>
        <img src={beforeUrl} alt={beforeLabel} className="block w-full h-full object-cover object-left" draggable="false" style={{ minWidth: wrap.current ? `${wrap.current.offsetWidth}px` : "100%" }} />
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur-sm text-white text-[11px] font-bold tracking-widest uppercase px-2.5 py-1">{beforeLabel}</div>
      <div className="absolute top-3 right-3 rounded-full bg-[#1B3626] text-white text-[11px] font-bold tracking-widest uppercase px-2.5 py-1">{afterLabel}</div>

      {/* Handle */}
      <button
        type="button"
        aria-label="Dra for å sammenligne"
        onKeyDown={onKey}
        onMouseDown={start}
        onTouchStart={start}
        className="absolute top-0 bottom-0 -translate-x-1/2 w-1 bg-white/95 cursor-ew-resize outline-none focus-visible:ring-2 focus-visible:ring-[#C49A6C]"
        style={{ left: `${pct}%` }}
        data-testid="before-after-handle"
      >
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg grid place-items-center">
          <GripVertical size={18} className="text-[#1B3626]" />
        </span>
      </button>
    </div>
  );
}
